import api from "./api-utils";

/**
 * Wire row from GET `/api/universalDescriptors/list` before normalization. The backend
 * historically exposes a human-readable label as `TagName`, which is easy to confuse
 * with an actual tag key.
 *
 * @typedef {Object} UniversalDescriptorListApiRow
 * @property {number} UniversalDescriptorId
 * @property {string} UD
 * @property {string} TagName - Human-readable label on the wire (mapped to `FriendlyName` in-app)
 * @property {boolean} [IsActive]
 */

/**
 * Normalized universal descriptor for app use (from {@link getUniversalDescriptors}).
 *
 * @typedef {Object} UniversalDescriptor
 * @property {number} UniversalDescriptorId - Unique identifier for the universal descriptor
 * @property {string} UD - Universal descriptor code
 * @property {string} FriendlyName - Human-readable label (from API `TagName` on the wire)
 * @property {boolean} IsActive - Whether the descriptor is active
 */

/**
 * @param {UniversalDescriptorListApiRow} row
 * @returns {UniversalDescriptor}
 */
function normalizeUniversalDescriptorListRow(row) {
  const { TagName, ...rest } = row;
  return {
    ...rest,
    FriendlyName: String(TagName).trim(),
  };
}

/**
 * Fetch all universal descriptors.
 * HOWEVER: omit `UniversalDescriptorId` **1** (Run Status) — that should not be not shown in pickers.
 * In the near future, there will likely be an exclude list of UDs (apart from run status, there are
 * other discreet value trends that may not make sense to show) but for now, at least exclude Run Status
 * since it is already included in the standard columns.
 *
 * @returns {Promise<UniversalDescriptor[]>} Normalized rows (see {@link UniversalDescriptor});
 *   the wire `TagName` field is mapped to `FriendlyName` and not returned on the object.
 */
export const getUniversalDescriptors = async () => {
  const response = await api.get("/api/universalDescriptors/list");
  const data = response.data;
  if (!Array.isArray(data)) return [];
  return data
    .filter((row) => Number(row?.UniversalDescriptorId) !== 1)
    .map((row) => normalizeUniversalDescriptorListRow(row));
};
